import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mic, MessageSquare, Zap, Shield, TrendingUp, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const MAQUITO_URL = "https://taglermaq.cl/wp-content/uploads/2026/03/maquito-levitando.svg";

const sectors = [
  {
    name: "Bakery",
    description: "Soluciones para panadería industrial",
    image: "https://taglermaq.cl/wp-content/uploads/2025/09/bakery_new.webp",
    gradient: "from-amber-500/20"
  },
  {
    name: "Cárnico",
    description: "Procesamiento de carnes",
    image: "https://taglermaq.cl/wp-content/uploads/2025/09/carnico_new.webp",
    gradient: "from-red-500/20 to-rose-500/20"
  },
  {
    name: "Lácteos",
    description: "Tecnología para lácteos",
    image: "https://taglermaq.cl/wp-content/uploads/2025/09/lacteos_new.webp",
    gradient: "from-blue-500/20 to-cyan-500/20"
  },
  {
    name: "Hortofrutícola",
    description: "Procesamiento de frutas",
    image: "https://taglermaq.cl/wp-content/uploads/2025/09/hortofruticola_new.webp",
    gradient: "from-green-500/20 to-emerald-500/20"
  },
  {
    name: "Pescados y Mariscos",
    description: "Soluciones acuícolas",
    image: "https://taglermaq.cl/wp-content/uploads/2025/09/seafood_new.jpg",
    gradient: "from-teal-500/20 to-blue-500/20"
  },
  {
    name: "Intralogística",
    description: "Automatización logística",
    image: "https://taglermaq.cl/wp-content/uploads/2025/09/intralogistica_new.webp",
    gradient: "from-slate-500/20 to-gray-500/20"
  }
];

const features = [
  {
    icon: MessageSquare,
    title: "Chat Inteligente",
    description: "Pregunta lo que necesites y recibe recomendaciones personalizadas"
  },
  {
    icon: Mic,
    title: "Entrada por Voz",
    description: "Habla con Maquito usando tu micrófono para una experiencia más rápida"
  },
  {
    icon: Zap,
    title: "Respuestas Instantáneas",
    description: "Recibe recomendaciones de máquinas en segundos"
  },
  {
    icon: Shield,
    title: "Experto Certificado",
    description: "Más de 26 años de experiencia en maquinaria industrial"
  }
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen gradient-bg" data-testid="landing-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_maquito-helper/artifacts/w7vfd1sr_maquito-sentado.png" alt="Maquito" className="h-12 w-12 rounded-full" style={{ height: 'auto' }} />
            <div>
              <h1 className="text-xl font-bold text-slate-900">Maquito</h1>
              <p className="text-xs text-slate-600">by Taglermaq</p>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/chat')} 
            className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6"
            data-testid="header-chat-button"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Comenzar Chat
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center" data-testid="hero-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.img 
            src={MAQUITO_URL} 
            alt="Maquito" 
            className="mx-auto mb-8 float-animation"
            style={{ width: '200px', height: 'auto', margin: '4em auto 0' }}
            data-testid="maquito-hero-image"
          />
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 mb-6">
            Tu experto en<br />
            <span className="text-green-600">maquinaria industrial</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            ¡Hola! Soy Maquito, tu asistente virtual de Taglermaq. Te ayudo a encontrar 
            la máquina perfecta para tu línea de producción. ¿Hablamos o escribimos?
          </p>
          
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              onClick={() => navigate('/chat')} 
              size="lg" 
              className="bg-green-600 hover:bg-green-700 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl"
              data-testid="hero-start-chat-button"
            >
              <MessageSquare className="h-5 w-5 mr-2" />
              Iniciar Conversación
            </Button>
            <Button 
              onClick={() => navigate('/chat')} 
              size="lg" 
              variant="outline" 
              className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-full px-8 py-6 text-lg"
              data-testid="hero-voice-button"
            >
              <Mic className="h-5 w-5 mr-2" />
              Hablar con Maquito
            </Button>
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16" data-testid="features-section">
        <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-12">
          ¿Por qué usar Maquito?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-slate-100"
                data-testid={`feature-card-${index}`}
              >
                <div className="bg-green-100 rounded-full p-3 w-fit mb-4">
                  <Icon className="h-6 w-6 text-green-700" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Sectors Bento Grid */}
      <section className="container mx-auto px-4 py-16" data-testid="sectors-section">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Sectores industriales
          </h2>
          <p className="text-lg text-slate-600">
            Encuentra tecnologías para optimizar tus líneas de procesamiento
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sectors.map((sector, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-2xl aspect-[4/3] group cursor-pointer bento-card"
              onClick={() => navigate('/chat')}
              data-testid={`sector-card-${index}`}
            >
              <img 
                src={sector.image} 
                alt={sector.name} 
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className={`absolute inset-0 bg-gradient-to-br ${sector.gradient} group-hover:opacity-80 transition-opacity`} />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-2xl font-bold mb-1">{sector.name}</h3>
                <p className="text-sm text-slate-200">{sector.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20 text-center" data-testid="cta-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-green-600 to-green-700 rounded-3xl p-12 shadow-2xl"
        >
          <TrendingUp className="h-16 w-16 mx-auto mb-6 text-white" />
          <h2 className="text-4xl font-bold text-white mb-4">
            ¿Listo para optimizar tu producción?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Comienza ahora y descubre las mejores soluciones en maquinaria industrial para tu negocio
          </p>
          <Button 
            onClick={() => navigate('/chat')} 
            size="lg" 
            className="bg-white text-green-700 hover:bg-green-50 rounded-full px-10 py-6 text-lg shadow-lg"
            data-testid="cta-button"
          >
            Hablar con Maquito ahora
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={MAQUITO_URL} alt="Maquito" className="h-10 w-10 rounded-full" />
            <span className="text-xl font-bold">Maquito by Taglermaq</span>
          </div>
          <p className="text-slate-400 mb-4">
            Más de 26 años de experiencia en la industria alimentaria
          </p>
          <p className="text-sm text-slate-500">
            © 2026 Taglermaq. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}